import { useState, useEffect } from 'react'
import {
  Clock, MapPin, Camera, AlertCircle, CheckCircle2, Sparkles, Compass,
  FileText, History, Briefcase, Info, TrendingUp, ArrowRight, LogIn, LogOut,
} from 'lucide-react'
import CameraCapture from '../../components/CameraCapture'
import GeolocationCapture from '../../components/GeolocationCapture'
import { useAuthStore } from '../../store/authStore'
import { attendanceAPI, reportAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'
import { formatTime, formatTimeWithSeconds } from '../../lib/dateFormat'

export default function EmployeeDashboard() {
  const { success: toastSuccess, error: toastError } = useToast()
  const { user } = useAuthStore()
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  const [isCheckedOut, setIsCheckedOut] = useState(false)
  const [showCamera, setShowCamera] = useState(false)
  const [showGeolocation, setShowGeolocation] = useState(false)
  const [cameraMode, setCameraMode] = useState<'checkin' | 'checkout'>('checkin')
  const [location, setLocation] = useState<any>(null)
  const [photo, setPhoto] = useState<string | null>(null)
  const [checkInTime, setCheckInTime] = useState<string | null>(null)
  const [checkOutTime, setCheckOutTime] = useState<string | null>(null)
  const [totalHours, setTotalHours] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [eodSubmitted, setEodSubmitted] = useState(false)
  const [now, setNow] = useState(new Date())

  useEffect(() => {
    const checkTodayStatus = async () => {
      try {
        const response = await attendanceAPI.getTodayStatus()
        if (response.data && response.data.check_in_time) {
          setIsCheckedIn(true)
          setCheckInTime(response.data.check_in_time)
          if (response.data.check_out_time) {
            setIsCheckedOut(true)
            setCheckOutTime(response.data.check_out_time)
            setTotalHours(response.data.total_hours ?? null)
          }
        }
      } catch (err) {
        // No check-in yet for today — expected on a fresh day.
      }
    }
    checkTodayStatus()

    reportAPI.getToday().then(() => setEodSubmitted(true)).catch(() => setEodSubmitted(false))

    const tick = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(tick)
  }, [])

  const handleCheckIn = async () => {
    setCameraMode('checkin')
    setShowCamera(true)
  }

  const handleCheckOut = async () => {
    if (!eodSubmitted) {
      toastError('Please submit today\'s End-of-Day report before checking out.')
      return
    }
    setCameraMode('checkout')
    setShowCamera(true)
  }

  const handlePhotoCapture = (photoData: string) => {
    setPhoto(photoData)
    setShowCamera(false)
    setShowGeolocation(true)
  }

  const handleLocationCapture = async (loc: any) => {
    setLocation(loc)
    setShowGeolocation(false)
    setIsSubmitting(true)
    setError(null)

    try {
      if (cameraMode === 'checkin') {
        const res = await attendanceAPI.checkIn(loc, photo || '')
        setIsCheckedIn(true)
        setCheckInTime(res.data.check_in_time)
        toastSuccess('Checked in successfully!')
      } else {
        const res = await attendanceAPI.checkOut(loc, photo || '')
        setIsCheckedOut(true)
        setCheckOutTime(res.data.check_out_time)
        setTotalHours(res.data.total_hours ?? null)
        toastSuccess('Checked out successfully! Have a great rest of your day.')
      }
    } catch (err: any) {
      const msg = getErrorMessage(err, 'Failed to record attendance. Please try again.')
      setError(msg)
      toastError(msg)
    } finally {
      setIsSubmitting(false)
      setPhoto(null)
      setLocation(null)
    }
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 17) return 'Good Afternoon'
    if (hour < 21) return 'Good Evening'
    return 'Good Night'
  }

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })

  // Status config — single source of truth for the pill + accent
  const status = isCheckedOut
    ? { label: 'Day Completed', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500', ring: 'ring-blue-200', bar: 'from-blue-500 to-blue-700' }
    : isCheckedIn
    ? { label: 'Checked In', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', ring: 'ring-emerald-200', bar: 'from-emerald-500 to-emerald-700' }
    : { label: 'Not Checked In', bg: 'bg-neutral-100', text: 'text-neutral-600', dot: 'bg-neutral-400', ring: 'ring-neutral-200', bar: 'from-[#F0821F] to-[#A31621]' }

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
            <Sparkles className="w-3 h-3" />
            My Workspace
          </div>
          <h1
            className="text-3xl font-bold text-neutral-900 mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {getGreeting()}, {user?.full_name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-sm text-neutral-500">
            Employee ID: <span className="font-medium text-neutral-700 tabular-nums">{user?.employee_id}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-neutral-200 rounded-xl px-3.5 py-2 shadow-sm">
          <Clock className="w-4 h-4 text-[#C24A0A]" />
          <span className="text-xs font-medium text-neutral-600">{today}</span>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mb-6 flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Attendance Card ── */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden mb-6">
        {/* Status accent bar */}
        <div className={`h-1 w-full bg-gradient-to-r ${status.bar}`} />

        <div className="p-6 sm:p-8">
          {/* Status pill row */}
          <div className="flex items-center justify-between gap-4 mb-6">
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ring-1 ${status.bg} ${status.text} ${status.ring}`}>
              <span className={`w-2 h-2 rounded-full ${status.dot} ${isCheckedIn && !isCheckedOut ? 'animate-pulse' : ''}`} />
              {status.label}
            </span>
            {totalHours != null && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-600">
                <TrendingUp className="w-3.5 h-3.5 text-[#C24A0A]" />
                <span className="tabular-nums">{totalHours}</span>
                <span className="text-neutral-400 font-normal">hours today</span>
              </span>
            )}
          </div>

          {/* Clock — the centerpiece */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F0821F] to-[#A31621] shadow-lg shadow-[#E4670F]/25 mb-4">
              <Clock className="w-7 h-7 text-white" />
            </div>
            <p className="text-5xl sm:text-6xl font-bold text-neutral-900 tabular-nums tracking-tight leading-none mb-2">
              {formatTimeWithSeconds(now)}
            </p>
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-neutral-400">
              Indian Standard Time
            </p>
          </div>

          {/* Timeline — check-in/out split */}
          {(checkInTime || checkOutTime) && (
            <div className="grid grid-cols-2 gap-3 mb-7">
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3.5">
                <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center shadow-sm flex-shrink-0">
                  <LogIn className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Check In</p>
                  <p className="text-base font-bold text-emerald-900 tabular-nums truncate">
                    {checkInTime ? formatTime(checkInTime) : '—'}
                  </p>
                </div>
              </div>
              <div className={`flex items-center gap-3 rounded-xl p-3.5 border ${
                checkOutTime ? 'bg-blue-50 border-blue-200' : 'bg-neutral-50 border-neutral-200'
              }`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0 ${
                  checkOutTime ? 'bg-blue-500' : 'bg-neutral-300'
                }`}>
                  <LogOut className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className={`text-[10px] font-semibold uppercase tracking-wide ${
                    checkOutTime ? 'text-blue-700' : 'text-neutral-500'
                  }`}>
                    Check Out
                  </p>
                  <p className={`text-base font-bold tabular-nums truncate ${
                    checkOutTime ? 'text-blue-900' : 'text-neutral-400'
                  }`}>
                    {checkOutTime ? formatTime(checkOutTime) : 'Pending'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action area */}
          <div className="max-w-md mx-auto space-y-3">
            {isCheckedOut ? (
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center shadow-md shadow-blue-500/30 flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-blue-900 mb-1">You're all done for today</p>
                    <p className="text-sm text-blue-800 leading-relaxed">
                      Checked in at <strong className="font-semibold">{checkInTime ? formatTime(checkInTime) : '—'}</strong> and
                      checked out at <strong className="font-semibold">{checkOutTime ? formatTime(checkOutTime) : '—'}</strong>
                      {totalHours != null ? (
                        <> — <strong className="font-semibold">{totalHours} hours</strong> logged.</>
                      ) : '.'}
                    </p>
                  </div>
                </div>
              </div>
            ) : !isCheckedIn ? (
              <button
                onClick={handleCheckIn}
                disabled={isSubmitting}
                className="group w-full flex items-center justify-center gap-2.5 px-6 py-4 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-semibold transition-all duration-300 shadow-md shadow-[#A31621]/20 hover:shadow-lg hover:shadow-[#A31621]/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                <Camera className="w-5 h-5 transition-transform group-hover:scale-110" />
                {isSubmitting ? 'Processing...' : 'Check In Now'}
              </button>
            ) : (
              <>
                <button
                  onClick={handleCheckOut}
                  disabled={isSubmitting}
                  title={!eodSubmitted ? "Submit today's EOD report first" : undefined}
                  className="group w-full flex items-center justify-center gap-2.5 px-6 py-4 bg-gradient-to-r from-[#F0821F] to-[#C24A0A] hover:from-[#E4670F] hover:to-[#A31621] text-white rounded-xl font-semibold transition-all duration-300 shadow-md shadow-[#E4670F]/30 hover:shadow-lg hover:shadow-[#E4670F]/40 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  <Camera className="w-5 h-5 transition-transform group-hover:scale-110" />
                  {isSubmitting ? 'Processing...' : 'Check Out Now'}
                </button>

                {!eodSubmitted && (
                  <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-xl px-3.5 py-2.5 text-xs text-orange-800">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      You'll need to{' '}
                      <a href="/employee/eod-report" className="underline font-semibold hover:text-orange-900">
                        submit today's EOD report
                      </a>{' '}
                      before checking out.
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Info Cards Grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Location Info */}
        <div className="group bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-5 hover:shadow-lg hover:ring-neutral-300/70 transition-all duration-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                Check-in Location
              </h3>
              <p className="text-[11px] text-neutral-500">GPS coordinates</p>
            </div>
          </div>
          {location ? (
            <div className="flex items-center gap-2.5 bg-neutral-50 rounded-lg px-3 py-2.5">
              <Compass className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
              <span className="text-xs font-semibold text-neutral-700 tabular-nums">
                {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
              </span>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-xs text-neutral-500 leading-relaxed">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-neutral-400" />
              <span>Location will be captured automatically during check-in.</span>
            </div>
          )}
        </div>

        {/* Important Notes */}
        <div className="group bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl ring-1 ring-orange-200/70 p-5 hover:shadow-lg hover:shadow-orange-500/5 transition-all duration-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center shadow-md shadow-orange-500/30 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-orange-900" style={{ fontFamily: 'Georgia, serif' }}>
                Important
              </h3>
              <p className="text-[11px] text-orange-700">Before you check out</p>
            </div>
          </div>
          <p className="text-xs text-orange-800 leading-relaxed">
            An <strong className="font-semibold">EOD report must be submitted</strong> before you can check out.
            Location verification is required.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="group bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl ring-1 ring-blue-200/70 p-5 hover:shadow-lg hover:shadow-blue-500/5 transition-all duration-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-md shadow-blue-500/30 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-blue-900" style={{ fontFamily: 'Georgia, serif' }}>
                Quick Actions
              </h3>
              <p className="text-[11px] text-blue-700">Common tasks</p>
            </div>
          </div>
          <div className="space-y-1.5">
            {[
              { href: '/employee/eod-report', label: 'Submit EOD Report', icon: FileText },
              { href: '/employee/history', label: 'View History', icon: History },
              { href: '/employee/leave', label: 'Request Leave', icon: Briefcase },
            ].map(({ href, label, icon: Icon }) => (
              <a
                key={href}
                href={href}
                className="group/link flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/60 hover:bg-white ring-1 ring-blue-200/60 hover:ring-blue-300 text-sm font-medium text-blue-700 hover:text-blue-900 transition-all"
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="flex-1 truncate">{label}</span>
                <ArrowRight className="w-3.5 h-3.5 flex-shrink-0 opacity-0 group-hover/link:opacity-100 group-hover/link:translate-x-0.5 transition-all" />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ── Camera Modal ── */}
      {showCamera && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <CameraCapture onCapture={handlePhotoCapture} onCancel={() => setShowCamera(false)} />
          </div>
        </div>
      )}

      {/* ── Geolocation Modal ── */}
      {showGeolocation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <GeolocationCapture onCapture={handleLocationCapture} onCancel={() => setShowGeolocation(false)} />
          </div>
        </div>
      )}
    </div>
  )
}